import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Badge, Tabs, Tab } from 'react-bootstrap';
import { FaArrowLeft, FaSave, FaTimes, FaSync, FaGithub, FaCloud, FaCheckCircle, FaTimesCircle, FaPython, FaCubes, FaNetworkWired, FaDatabase, FaGlobe, FaBug } from 'react-icons/fa';
import { useEnvironment } from '../context/EnvironmentContext';
import Spinner from '../components/Spinner';
import * as api from '../services/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Configuration.css';

function Configuration() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { activeEnvironment } = useEnvironment();
  
  // State management
  const [agentConfig, setAgentConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('metadata');
  
  // Form data for editable fields
  const [editableData, setEditableData] = useState({});
  const [githubData, setGithubData] = useState({});

  useEffect(() => {
    fetchAgentConfiguration();
  }, [agentId]);

  useEffect(() => {
    // Check if there are unsaved changes
    if (originalConfig && (
      JSON.stringify(editableData) !== JSON.stringify(originalConfig.user_editable) ||
      Object.keys(githubData).length > 0
    )) {
      setHasChanges(true);
    } else {
      setHasChanges(false);
    }
  }, [editableData, githubData, originalConfig]);

  const fetchAgentConfiguration = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.makeAuthenticatedRequest('POST', '/config', {
        action: 'get_agent',
        agent_id: agentId,
        categorize: true
      });

      // Handle response structure (checking both direct agent prop and nested data.agent)
      const agentData = response?.agent || response?.data?.agent;

      if (response && response.success && agentData) {
        setAgentConfig(agentData);
        setOriginalConfig(agentData);
        setEditableData(agentData.user_editable || {});
        setGithubData({});
      } else {
        throw new Error(response?.error?.message || 'Failed to fetch agent configuration');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching agent configuration:', err);
      setError('Failed to load agent configuration: ' + err.message);
      setLoading(false);
    }
  };

  const handleEditableChange = (field, value) => {
    setEditableData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGithubChange = (field, value) => {
    setGithubData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGithubArrayChange = (field, valueString) => {
    // Split string by newline to create array, filter out empty strings
    const arrayValue = valueString.split('\n').map(s => s.trim()).filter(s => s);
    setGithubData(prev => ({
      ...prev,
      [field]: arrayValue
    }));
  };

  const handleGithubCheckboxChange = (field, checked) => {
    setGithubData(prev => ({
      ...prev,
      [field]: checked
    }));
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Determine which updates to make
      const hasDatabaseUpdates = JSON.stringify(editableData) !== JSON.stringify(originalConfig.user_editable);
      const hasGithubUpdates = Object.keys(githubData).length > 0;

      let response;

      if (hasDatabaseUpdates && hasGithubUpdates) {
        // Update both
        response = await api.makeAuthenticatedRequest('POST', '/config', {
          action: 'update_both',
          agent_id: agentId,
          database_updates: editableData,
          github_updates: githubData
        });
      } else if (hasDatabaseUpdates) {
        // Update database only
        response = await api.makeAuthenticatedRequest('POST', '/config', {
          action: 'update_database',
          agent_id: agentId,
          updates: editableData
        });
      } else if (hasGithubUpdates) {
        // Update GitHub only
        response = await api.makeAuthenticatedRequest('POST', '/config', {
          action: 'update_github',
          agent_id: agentId,
          updates: githubData
        });
      } else {
        setSaving(false);
        return;
      }

      if (response && response.success) {
        setSuccess('Configuration updated successfully!');
        toast.success('Configuration updated successfully!');
        
        // Reset github data and refresh configuration
        setGithubData({});
        await fetchAgentConfiguration();
      } else {
        throw new Error(response?.error?.message || 'Failed to update configuration');
      }
    } catch (err) {
      console.error('Error updating configuration:', err);
      setError('Failed to update configuration: ' + err.message);
      toast.error('Failed to update configuration: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelChanges = () => {
    setEditableData(originalConfig.user_editable || {});
    setGithubData({});
    setHasChanges(false);
  };

  const handleRefresh = () => {
    fetchAgentConfiguration();
  };

  const handleBackClick = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(`/parent/${agentId}`);
      }
    } else {
      navigate(`/parent/${agentId}`);
    }
  };

  // Helper to get current value for a GitHub field (from unsaved changes or original config)
  const getGithubValue = (field) => {
    if (githubData[field] !== undefined) {
      return githubData[field];
    }
    return agentConfig?.github_synced?.[field];
  };

  // Render an editable text field that syncs to GitHub
  const renderEditableGithubField = (label, field, placeholder = '') => {
    const value = getGithubValue(field);
    return (
      <Form.Group className="mb-3">
        <Form.Label>{label}</Form.Label>
        <div className="input-group">
          <span className="input-group-text bg-dark border-secondary text-muted">
            <FaGithub size={12} />
          </span>
          <Form.Control 
            type="text" 
            value={value || ''} 
            onChange={(e) => handleGithubChange(field, e.target.value)}
            placeholder={placeholder}
            className="github-editable-field"
          />
        </div>
      </Form.Group>
    );
  };

  // Render an editable checkbox that syncs to GitHub
  const renderEditableGithubCheckbox = (label, field) => {
    const checked = getGithubValue(field) === true;
    return (
      <Form.Group className="mb-3">
        <Form.Label>{label}</Form.Label>
        <div className="d-flex align-items-center mt-2">
          <FaGithub size={12} className="text-muted me-2" />
          <Form.Check 
            type="switch"
            id={`switch-${field}`}
            checked={checked} 
            onChange={(e) => handleGithubCheckboxChange(field, e.target.checked)}
            label={checked ? "Enabled" : "Disabled"}
          />
        </div>
      </Form.Group>
    );
  };

  // Render an editable textarea for arrays (one item per line)
  const renderEditableGithubArray = (label, field, rows = 5) => {
    const value = getGithubValue(field);
    const textValue = Array.isArray(value) ? value.join('\n') : (value || '');
    
    return (
      <Form.Group className="mb-3">
        <Form.Label>{label} <span className="text-muted small">(One per line)</span></Form.Label>
        <div className="input-group">
          <span className="input-group-text bg-dark border-secondary text-muted align-items-start pt-2">
            <FaGithub size={12} />
          </span>
          <Form.Control 
            as="textarea" 
            rows={rows}
            value={textValue} 
            onChange={(e) => handleGithubArrayChange(field, e.target.value)}
            className="github-editable-field"
          />
        </div>
      </Form.Group>
    );
  };

  // Helper to render a read-only field (for Computed/System/Metadata)
  const renderReadOnlyField = (label, value, icon = null) => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <div className="input-group">
        {icon && (
          <span className="input-group-text bg-dark border-secondary text-muted">
            {icon}
          </span>
        )}
        <Form.Control 
          type="text" 
          value={value || ''} 
          disabled 
          className="read-only-field"
        />
      </div>
    </Form.Group>
  );

  // Helper to render a read-only array field (for Python Layers)
  const renderReadOnlyArrayField = (label, value, icon = null, rows = 5) => {
    const textValue = Array.isArray(value) ? value.join('\n') : (value || '');
    return (
      <Form.Group className="mb-3">
        <Form.Label>{label}</Form.Label>
        <div className="input-group">
          {icon && (
            <span className="input-group-text bg-dark border-secondary text-muted align-items-start pt-2">
              {icon}
            </span>
          )}
          <Form.Control 
            as="textarea"
            rows={rows}
            value={textValue} 
            disabled 
            className="read-only-field"
          />
        </div>
      </Form.Group>
    );
  };

  if (loading) {
    return (
      <Container className="py-4 d-flex justify-content-center align-items-center" style={{ minHeight: '70vh' }}>
        <div className="text-center">
          <Spinner />
          <p className="mt-3">Loading configuration...</p>
        </div>
      </Container>
    );
  }

  if (error && !agentConfig) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Configuration</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-primary" onClick={handleBackClick}>
            Back to Agent
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="configuration-page py-4">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="page-title">Agent Configuration</h1>
              <p className="page-description">
                Configure settings for {agentConfig?.metadata?.agent_name || agentId}
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" onClick={handleBackClick}>
                <FaArrowLeft className="me-2" />
                Back
              </Button>
              <Button 
                variant="outline-info" 
                onClick={handleRefresh}
                disabled={loading}
              >
                <FaSync className="me-2" />
                Refresh
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {hasChanges && (
        <Alert variant="warning" className="mb-4 sticky-top shadow-sm" style={{ zIndex: 100 }}>
          <div className="d-flex justify-content-between align-items-center">
            <span>You have unsaved changes</span>
            <div>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={handleCancelChanges}
                className="me-2"
              >
                <FaTimes className="me-1" />
                Cancel
              </Button>
              <Button 
                variant="primary" 
                size="sm" 
                onClick={handleSaveChanges}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="me-1" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4" onClose={() => setSuccess(null)} dismissible>
          {success}
        </Alert>
      )}

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4 configuration-tabs"
      >
        {/* Metadata Tab - Read Only */}
        <Tab eventKey="metadata" title="Agent Information">
          <Card>
            <Card.Body>
              <h4 className="mb-3">Agent Metadata (Read Only)</h4>
              <Row>
                <Col md={6}>
                  {renderReadOnlyField("Agent ID", agentConfig?.metadata?.agent_id)}
                </Col>
                <Col md={6}>
                  {renderReadOnlyField("Agent Name", agentConfig?.metadata?.agent_name)}
                </Col>
                <Col md={6}>
                  {renderReadOnlyField("GitHub Account", agentConfig?.metadata?.github_account, <FaGithub />)}
                </Col>
                <Col md={6}>
                  {renderReadOnlyField("GitHub Repository", agentConfig?.metadata?.github_repo, <FaGithub />)}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        {/* General Settings Tab - Editable */}
        <Tab eventKey="general" title="General Settings">
          <Card>
            <Card.Body>
              <h4 className="mb-3">General Settings</h4>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control 
                      as="textarea"
                      rows={3}
                      value={editableData.description || ''} 
                      onChange={(e) => handleEditableChange('description', e.target.value)}
                      className="field-full-width"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select 
                      value={editableData.status || 'new'}
                      onChange={(e) => handleEditableChange('status', e.target.value)}
                      className="field-medium-width"
                    >
                      <option value="new">New</option>
                      <option value="developing">Developing</option>
                      <option value="production">Production</option>
                      <option value="deprecated">Deprecated</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Master Mini Agent (3 digits)</Form.Label>
                    <Form.Control 
                      type="text" 
                      maxLength={3}
                      pattern="[0-9]{3}"
                      value={editableData.master_mini_agent || ''} 
                      onChange={(e) => handleEditableChange('master_mini_agent', e.target.value)}
                      placeholder="e.g., 901"
                      className="field-short-width"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Conversation Micro Agent (3 digits)</Form.Label>
                    <Form.Control 
                      type="text" 
                      maxLength={3}
                      pattern="[0-9]{3}"
                      value={editableData.conversation_micro_agent || ''} 
                      onChange={(e) => handleEditableChange('conversation_micro_agent', e.target.value)}
                      placeholder="Optional"
                      className="field-short-width"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        {/* Frontend Configuration Tab - Editable */}
        <Tab eventKey="frontend" title="Frontend Config">
          <Card>
            <Card.Body>
              <h4 className="mb-3">Frontend Configuration</h4>
              <Alert variant="info" className="mb-4">
                <FaGlobe className="me-2" />
                These settings control the frontend hosting and authentication for your agent's web interface.
              </Alert>
              
              <h5 className="mt-4 mb-3 text-primary">Development Environment</h5>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Website Domain</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editableData.frontend_website_domain_dev || ''} 
                      onChange={(e) => handleEditableChange('frontend_website_domain_dev', e.target.value)}
                      placeholder="dev-app.example.com"
                      className="field-medium-width"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>S3 Bucket</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editableData.frontend_website_s3_bucket_dev || ''} 
                      onChange={(e) => handleEditableChange('frontend_website_s3_bucket_dev', e.target.value)}
                      placeholder="dev-bucket"
                      className="field-medium-width"
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>CloudFront ARN</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editableData.frontend_cloudfront_distribution_arn_dev || ''} 
                      onChange={(e) => handleEditableChange('frontend_cloudfront_distribution_arn_dev', e.target.value)}
                      placeholder="arn:aws:cloudfront::..."
                      className="field-full-width"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>User Pool ID</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editableData.frontend_user_pool_id_dev || ''} 
                      onChange={(e) => handleEditableChange('frontend_user_pool_id_dev', e.target.value)}
                      placeholder="us-west-2_xxx"
                      className="field-medium-width"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>User Pool Client</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editableData.frontend_user_pool_client_dev || ''} 
                      onChange={(e) => handleEditableChange('frontend_user_pool_client_dev', e.target.value)}
                      placeholder="xxxxx"
                      className="field-medium-width"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Identity Pool</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editableData.frontend_identity_pool_dev || ''} 
                      onChange={(e) => handleEditableChange('frontend_identity_pool_dev', e.target.value)}
                      placeholder="us-west-2:xxx"
                      className="field-medium-width"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <h5 className="mt-4 mb-3 text-primary">Production Environment</h5>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Website Domain</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editableData.frontend_website_domain_prod || ''} 
                      onChange={(e) => handleEditableChange('frontend_website_domain_prod', e.target.value)}
                      placeholder="app.example.com"
                      className="field-medium-width"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>S3 Bucket</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editableData.frontend_website_s3_bucket_prod || ''} 
                      onChange={(e) => handleEditableChange('frontend_website_s3_bucket_prod', e.target.value)}
                      placeholder="prod-bucket"
                      className="field-medium-width"
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>CloudFront ARN</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editableData.frontend_cloudfront_distribution_arn_prod || ''} 
                      onChange={(e) => handleEditableChange('frontend_cloudfront_distribution_arn_prod', e.target.value)}
                      placeholder="arn:aws:cloudfront::..."
                      className="field-full-width"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>User Pool ID</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editableData.frontend_user_pool_id_prod || ''} 
                      onChange={(e) => handleEditableChange('frontend_user_pool_id_prod', e.target.value)}
                      placeholder="us-west-2_xxx"
                      className="field-medium-width"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>User Pool Client</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editableData.frontend_user_pool_client_prod || ''} 
                      onChange={(e) => handleEditableChange('frontend_user_pool_client_prod', e.target.value)}
                      placeholder="xxxxx"
                      className="field-medium-width"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Identity Pool</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editableData.frontend_identity_pool_prod || ''} 
                      onChange={(e) => handleEditableChange('frontend_identity_pool_prod', e.target.value)}
                      placeholder="us-west-2:xxx"
                      className="field-medium-width"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        {/* Backend Configuration Tab - Editable */}
        <Tab eventKey="backend" title="Backend Config">
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Backend Configuration</h4>
                <Badge bg="success">GitHub Synced - Editable</Badge>
              </div>
              <Alert variant="info">
                <FaNetworkWired className="me-2" />
                These settings define the core backend domains and cognito pools. Changes here will be committed to <code>config.ts</code> in GitHub.
              </Alert>

              <h5 className="mt-4 mb-3 text-primary">Development Environment</h5>
              <Row>
                <Col md={6}>{renderEditableGithubField("Core Domain", "core_domain_dev")}</Col>
                <Col md={6}>{renderEditableGithubField("Core Subdomain", "core_subdomain_dev")}</Col>
                <Col md={4}>{renderEditableGithubField("Core User Pool", "core_user_pool_id_dev")}</Col>
                <Col md={4}>{renderEditableGithubField("Core Client ID", "core_user_pool_client_dev")}</Col>
                <Col md={4}>{renderEditableGithubField("Core Identity Pool", "core_identity_pool_dev")}</Col>
                
                <Col md={12} className="my-2"><hr/></Col>
                
                <Col md={6}>{renderEditableGithubField("API Domain", "api_domain_dev")}</Col>
                <Col md={6}>{renderEditableGithubField("API Subdomain", "api_subdomain_dev")}</Col>
                <Col md={4}>{renderEditableGithubField("API User Pool", "api_user_pool_id_dev")}</Col>
                <Col md={4}>{renderEditableGithubField("API Client ID", "api_user_pool_client_dev")}</Col>
                <Col md={4}>{renderEditableGithubField("API Identity Pool", "api_identity_pool_dev")}</Col>

                <Col md={12} className="my-2"><hr/></Col>
                
                <Col md={6}>{renderEditableGithubField("Conversation Domain", "conversation_domain_dev")}</Col>
                <Col md={6}>{renderEditableGithubField("Conversation Subdomain", "conversation_subdomain_dev")}</Col>
                <Col md={6}>{renderEditableGithubField("API Test Username", "api_test_dev_username")}</Col>
                <Col md={6}>{renderEditableGithubField("API Test Password", "api_test_dev_password")}</Col>
              </Row>

              <h5 className="mt-4 mb-3 text-primary">Production Environment</h5>
              <Row>
                <Col md={6}>{renderEditableGithubField("Core Domain", "core_domain_prod")}</Col>
                <Col md={6}>{renderEditableGithubField("Core Subdomain", "core_subdomain_prod")}</Col>
                <Col md={4}>{renderEditableGithubField("Core User Pool", "core_user_pool_id_prod")}</Col>
                <Col md={4}>{renderEditableGithubField("Core Client ID", "core_user_pool_client_prod")}</Col>
                <Col md={4}>{renderEditableGithubField("Core Identity Pool", "core_identity_pool_prod")}</Col>
                
                <Col md={12} className="my-2"><hr/></Col>
                
                <Col md={6}>{renderEditableGithubField("API Domain", "api_domain_prod")}</Col>
                <Col md={6}>{renderEditableGithubField("API Subdomain", "api_subdomain_prod")}</Col>
                <Col md={4}>{renderEditableGithubField("API User Pool", "api_user_pool_id_prod")}</Col>
                <Col md={4}>{renderEditableGithubField("API Client ID", "api_user_pool_client_prod")}</Col>
                <Col md={4}>{renderEditableGithubField("API Identity Pool", "api_identity_pool_prod")}</Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        {/* GitHub Integration Tab - Editable */}
        <Tab eventKey="github" title="GitHub Integration">
          <Card>
            <Card.Body>
              <h4 className="mb-3">GitHub Integration</h4>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Frontend GitHub Account</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text bg-dark border-secondary text-muted"><FaGithub /></span>
                      <Form.Control 
                        type="text" 
                        value={editableData.frontend_github_account || ''} 
                        onChange={(e) => handleEditableChange('frontend_github_account', e.target.value)}
                        placeholder="Optional"
                        className="field-medium-width"
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Frontend GitHub Repository</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text bg-dark border-secondary text-muted"><FaGithub /></span>
                      <Form.Control 
                        type="text" 
                        value={editableData.frontend_github_repository || ''} 
                        onChange={(e) => handleEditableChange('frontend_github_repository', e.target.value)}
                        placeholder="Optional"
                        className="field-medium-width"
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>GitHub Token Secret Name</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editableData.github_token_secret_name || ''} 
                      onChange={(e) => handleEditableChange('github_token_secret_name', e.target.value)}
                      placeholder="GitHubToken"
                      className="field-medium-width"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>GitHub Token Secret Key</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editableData.github_token_secret_key || ''} 
                      onChange={(e) => handleEditableChange('github_token_secret_key', e.target.value)}
                      placeholder="token"
                      className="field-medium-width"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        {/* Infrastructure Tab - Editable */}
        <Tab eventKey="infrastructure" title="Infrastructure">
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Infrastructure Configuration</h4>
                <Badge bg="success">GitHub Synced - Editable</Badge>
              </div>
              <Alert variant="info">
                <FaCloud className="me-2" />
                These settings define the AWS infrastructure configuration.
              </Alert>

              <h5 className="mt-4 mb-3 text-primary">AWS Configuration</h5>
              <Row>
                <Col md={6}>{renderEditableGithubField("GitHub Role ARN", "aws_github_role")}</Col>
                <Col md={6}>{renderEditableGithubField("Lambda Runtime", "lambda_runtime")}</Col>
                <Col md={6}>{renderEditableGithubField("VPC Name", "vpc_name")}</Col>
                <Col md={6}>{renderEditableGithubField("Global WAF Name", "global_waf_name")}</Col>
                <Col md={6}>{renderEditableGithubField("AWS Account (Dev)", "aws_account_dev")}</Col>
                <Col md={6}>{renderEditableGithubField("AWS Region (Dev)", "aws_region_dev")}</Col>
                <Col md={6}>{renderEditableGithubField("AWS Account (Prod)", "aws_account_prod")}</Col>
                <Col md={6}>{renderEditableGithubField("AWS Region (Prod)", "aws_region_prod")}</Col>
              </Row>

              <h5 className="mt-4 mb-3 text-primary">Step Functions Configuration</h5>
              <Row>
                <Col md={6}>{renderEditableGithubField("Shared Role Name", "step_functions_shared_role_name")}</Col>
                <Col md={6}>
                  {renderEditableGithubCheckbox("Enable Shared Role", "step_functions_enable_shared_role")}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        {/* Database Tab - Editable */}
        <Tab eventKey="database" title="Database">
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Database Configuration</h4>
                <Badge bg="success">GitHub Synced - Editable</Badge>
              </div>
              <Alert variant="info">
                <FaDatabase className="me-2" />
                Configure Aurora database settings and Vector RAG knowledge systems.
              </Alert>
              
              <h5 className="mt-4 mb-3 text-primary">Aurora Learnings Cluster</h5>
              <Row>
                <Col md={12}>{renderEditableGithubField("Cluster ARN", "aurora_learnings_cluster_arn")}</Col>
                <Col md={12}>{renderEditableGithubField("Cluster Endpoint", "aurora_learnings_cluster_endpoint")}</Col>
                <Col md={12}>{renderEditableGithubField("Secret ARN", "aurora_learnings_secret_arn")}</Col>
                <Col md={6}>{renderEditableGithubField("Security Group ID", "aurora_learnings_security_group_id")}</Col>
                <Col md={6}>{renderEditableGithubField("Dev Database Name", "aurora_learnings_dev_database_name")}</Col>
                <Col md={6}>{renderEditableGithubField("Prod Database Name", "aurora_learnings_prod_database_name")}</Col>
                
                <Col md={4}>{renderEditableGithubField("Vector Dimensions", "aurora_learnings_vector_dimensions")}</Col>
                <Col md={4}>{renderEditableGithubField("Embedding Model", "aurora_learnings_embedding_model")}</Col>
                <Col md={4}>
                  {renderEditableGithubCheckbox("Enable Vector Extension", "aurora_learnings_enable_vector_extension")}
                </Col>
              </Row>

              <h5 className="mt-4 mb-3 text-primary">Knowledge System Configuration (Dev)</h5>
              <Row>
                <Col md={3}>
                  {renderEditableGithubCheckbox("Use Aurora Vector RAG", "knowledge_use_aurora_vector_rag_dev")}
                </Col>
                <Col md={3}>{renderEditableGithubField("Vector Dimensions", "knowledge_vector_dimensions_dev")}</Col>
                <Col md={3}>{renderEditableGithubField("Embedding Model", "knowledge_embedding_model_dev")}</Col>
                <Col md={3}>{renderEditableGithubField("LLM Router Level", "knowledge_llm_router_level_dev")}</Col>
              </Row>

              <h5 className="mt-4 mb-3 text-primary">Knowledge System Configuration (Prod)</h5>
              <Row>
                <Col md={3}>
                  {renderEditableGithubCheckbox("Use Aurora Vector RAG", "knowledge_use_aurora_vector_rag_prod")}
                </Col>
                <Col md={3}>{renderEditableGithubField("Vector Dimensions", "knowledge_vector_dimensions_prod")}</Col>
                <Col md={3}>{renderEditableGithubField("Embedding Model", "knowledge_embedding_model_prod")}</Col>
                <Col md={3}>{renderEditableGithubField("LLM Router Level", "knowledge_llm_router_level_prod")}</Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        {/* CDK & Build Tab - Editable */}
        <Tab eventKey="cdk" title="CDK & Build">
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">CDK & Build Configuration</h4>
                <Badge bg="success">GitHub Synced - Editable</Badge>
              </div>
              <Alert variant="info">
                <FaCubes className="me-2" />
                Configuration for AWS CDK deployment and Docker assets.
              </Alert>

              <h5 className="mt-4 mb-3 text-primary">Project Settings</h5>
              <Row>
                <Col md={4}>{renderEditableGithubField("Stack Prefix", "stack_prefix")}</Col>
                <Col md={4}>{renderEditableGithubField("Build Project", "build_project")}</Col>
                <Col md={4}>{renderEditableGithubField("Backup Bucket", "backup_bucket")}</Col>
                <Col md={6}>{renderEditableGithubField("Node Options", "node_options", "--max-old-space-size=4096")}</Col>
              </Row>

              <h5 className="mt-4 mb-3 text-primary">CDK Settings</h5>
              <Row>
                <Col md={6}>{renderEditableGithubField("CDK Qualifier", "cdk_qualifier")}</Col>
                <Col md={6}>{renderEditableGithubField("Toolkit Stack Name", "cdk_toolkit_stack_name")}</Col>
                <Col md={6}>{renderEditableGithubField("Asset Prefix", "cdk_asset_prefix")}</Col>
                <Col md={6}>{renderEditableGithubField("Bucket Prefix", "cdk_bucket_prefix")}</Col>
                <Col md={6}>{renderEditableGithubField("Docker Tag Prefix", "cdk_docker_tag_prefix")}</Col>
                <Col md={6}>{renderEditableGithubField("File Assets Bucket", "cdk_file_assets_bucket_name")}</Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        {/* Python Layers Tab - Read Only */}
        <Tab eventKey="python" title="Python Layers">
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Python Lambda Layers</h4>
                <Badge bg="secondary">Read Only</Badge>
              </div>
              <Alert variant="info">
                <FaPython className="me-2" />
                Library definitions for Python Lambda layers. These are read-only configuration.
              </Alert>

              <Row>
                <Col md={12}>
                  {renderReadOnlyArrayField("LLM Layer Libraries", agentConfig?.github_synced?.llm_layer_python_libraries, <FaGithub />)}
                </Col>
                <Col md={12}>
                  {renderReadOnlyArrayField("API Layer Libraries", agentConfig?.github_synced?.api_layer_python_libraries, <FaGithub />)}
                </Col>
                <Col md={12}>
                  {renderReadOnlyArrayField("Knowledge Layer Libraries", agentConfig?.github_synced?.knowledge_layer_python_libraries, <FaGithub />)}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        {/* Computed Resources Tab - Read Only */}
        <Tab eventKey="computed" title="Computed Resources">
          <Card>
            <Card.Body>
              <h4 className="mb-3">Computed Resources (Read Only)</h4>
              <Alert variant="info">
                <FaCloud className="me-2" />
                These resources are automatically computed and managed by the system.
              </Alert>

              <h5 className="mt-4 mb-3 text-primary">Development Environment</h5>
              <Row>
                <Col md={6}>{renderReadOnlyField("Core Stack Name", agentConfig?.computed?.core_stack_name_dev)}</Col>
                <Col md={6}>{renderReadOnlyField("Accessories Stack Name", agentConfig?.computed?.accessories_stack_name_dev)}</Col>
                <Col md={6}>{renderReadOnlyField("API Stack Name", agentConfig?.computed?.api_stack_name_dev)}</Col>
                <Col md={6}>{renderReadOnlyField("Shared Secret Name", agentConfig?.computed?.shared_secret_name_dev)}</Col>
                <Col md={6}>{renderReadOnlyField("LLM Router Function Name", agentConfig?.computed?.llm_router_function_name_dev)}</Col>
                <Col md={6}>{renderReadOnlyField("S3 Core Bucket Name", agentConfig?.computed?.s3_bucket_core_name_dev)}</Col>
              </Row>
              
              <h5 className="mt-4 mb-3 text-primary">Production Environment</h5>
              <Row>
                <Col md={6}>{renderReadOnlyField("Core Stack Name", agentConfig?.computed?.core_stack_name_prod)}</Col>
                <Col md={6}>{renderReadOnlyField("Accessories Stack Name", agentConfig?.computed?.accessories_stack_name_prod)}</Col>
                <Col md={6}>{renderReadOnlyField("API Stack Name", agentConfig?.computed?.api_stack_name_prod)}</Col>
                <Col md={6}>{renderReadOnlyField("Shared Secret Name", agentConfig?.computed?.shared_secret_name_prod)}</Col>
                <Col md={6}>{renderReadOnlyField("LLM Router Function Name", agentConfig?.computed?.llm_router_function_name_prod)}</Col>
                <Col md={6}>{renderReadOnlyField("S3 Core Bucket Name", agentConfig?.computed?.s3_bucket_core_name_prod)}</Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        {/* Deployment Status Tab - Read Only */}
        <Tab eventKey="deployment" title="Deployment Status">
          <Card>
            <Card.Body>
              <h4 className="mb-3">Deployment Status (AWS Managed)</h4>
              <Alert variant="info">
                <FaCheckCircle className="me-2" />
                Deployment status is automatically updated by AWS deployment processes.
              </Alert>

              <Row>
                <Col md={6}>
                  <Card className="mb-3 h-100">
                    <Card.Header>
                      <h5 className="mb-0">Development Environment</h5>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Core Deployed:</span>
                        {agentConfig?.aws_managed?.core_deployed_development ? (
                          <Badge bg="success"><FaCheckCircle className="me-1" /> Deployed</Badge>
                        ) : (
                          <Badge bg="secondary"><FaTimesCircle className="me-1" /> Not Deployed</Badge>
                        )}
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>API Deployed:</span>
                        {agentConfig?.aws_managed?.api_deployed_development ? (
                          <Badge bg="success"><FaCheckCircle className="me-1" /> Deployed</Badge>
                        ) : (
                          <Badge bg="secondary"><FaTimesCircle className="me-1" /> Not Deployed</Badge>
                        )}
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Accessories Deployed:</span>
                        {agentConfig?.aws_managed?.accessories_deployed_development ? (
                          <Badge bg="success"><FaCheckCircle className="me-1" /> Deployed</Badge>
                        ) : (
                          <Badge bg="secondary"><FaTimesCircle className="me-1" /> Not Deployed</Badge>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="mb-3 h-100">
                    <Card.Header>
                      <h5 className="mb-0">Production Environment</h5>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Core Deployed:</span>
                        {agentConfig?.aws_managed?.core_deployed_production ? (
                          <Badge bg="success"><FaCheckCircle className="me-1" /> Deployed</Badge>
                        ) : (
                          <Badge bg="secondary"><FaTimesCircle className="me-1" /> Not Deployed</Badge>
                        )}
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>API Deployed:</span>
                        {agentConfig?.aws_managed?.api_deployed_production ? (
                          <Badge bg="success"><FaCheckCircle className="me-1" /> Deployed</Badge>
                        ) : (
                          <Badge bg="secondary"><FaTimesCircle className="me-1" /> Not Deployed</Badge>
                        )}
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Accessories Deployed:</span>
                        {agentConfig?.aws_managed?.accessories_deployed_production ? (
                          <Badge bg="success"><FaCheckCircle className="me-1" /> Deployed</Badge>
                        ) : (
                          <Badge bg="secondary"><FaTimesCircle className="me-1" /> Not Deployed</Badge>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        {/* System Information Tab - Read Only */}
        <Tab eventKey="system" title="System Info">
          <Card>
            <Card.Body>
              <h4 className="mb-3">System Information (Read Only)</h4>
              <Row>
                <Col md={6}>{renderReadOnlyField("Last Code Commit", agentConfig?.system?.last_code_commit, <FaGithub />)}</Col>
                <Col md={6}>{renderReadOnlyField("Last Updated", agentConfig?.system?.last_updated)}</Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Active Status</Form.Label>
                    <div className="mt-2">
                      {agentConfig?.system?.active ? (
                        <Badge bg="success"><FaCheckCircle /> Active</Badge>
                      ) : (
                        <Badge bg="secondary"><FaTimesCircle /> Inactive</Badge>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>{renderReadOnlyField("Created At", agentConfig?.system?.created_at)}</Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {hasChanges && (
        <Card className="mt-4 border-warning">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1 text-warning">Unsaved Changes</h5>
                <p className="mb-0 text-muted">You have unsaved changes in your configuration. Save or cancel to continue.</p>
              </div>
              <div>
                <Button 
                  variant="outline-secondary" 
                  onClick={handleCancelChanges}
                  className="me-2"
                >
                  <FaTimes className="me-2" />
                  Cancel Changes
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleSaveChanges}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      Save All Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}

export default Configuration;